import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { REMINDER_TRIGGERS, triggerInfo, type ReminderTrigger } from "@rentorng/core";
import { api } from "@/lib/api";
import { pluralise, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { MessageStatusBadge } from "@/components/status";
import { MeterBar } from "@/components/chart";
import { IconOneEnvelope } from "@/components/icons";
import { Alert, Badge, Button, Card, CardTitle, EmptyState, ErrorState, Field, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";

/**
 * The reminder engine's control room.
 *
 * Three things, in this order: what *would* go out on the next automatic run, the rules that
 * decide it, and the log of everything already sent. The preview is first deliberately —
 * automation nobody can inspect before it runs is automation nobody switches on.
 */

interface RulesResponse {
  rules: Array<{
    rule: { id: string; trigger: string; offsetDays: number; channel: string; templateId: string | null; enabled: boolean };
    templateName: string | null;
  }>;
  templates: Array<{ id: string; name: string; trigger: string | null; channel: string }>;
  gate: { current: { name: string }; needed: { name: string } | null } | null;
  quota: { used: number; limit: number; allowed: boolean; nearLimit: boolean };
  autoEnabled: boolean;
  sendWindow: { from: number; to: number; timezone: string };
  /** One message per invoice, or one per client. See `REMINDER_STYLES` in `@rentorng/core`. */
  style: "per_invoice" | "per_person";
  styles: Array<{ value: "per_invoice" | "per_person"; label: string; description: string }>;
}

interface PreviewResponse {
  planned: Array<{
    ruleId: string;
    trigger: string;
    offsetDays: number;
    channel: string;
    anchorDate: string;
    party: { id: string; name: string; email: string | null; phone: string | null } | null;
    deliverable: boolean;
    /** How many invoices this one message itemises. One means an ordinary reminder. */
    covers: number;
    total: number | null;
  }>;
  style: "per_invoice" | "per_person";
  today: string;
}

export function RemindersPage() {
  const { can } = useSession();
  const fmt = useFormat();
  const [tab, setTab] = useState<"preview" | "rules" | "log">("preview");
  const [adding, setAdding] = useState(false);

  const rules = useQuery({ queryKey: ["reminder-rules"], queryFn: () => api.get<RulesResponse>("/reminders/rules") });
  const preview = useQuery({ queryKey: ["reminder-preview"], queryFn: () => api.get<PreviewResponse>("/reminders/preview") });
  const messages = useQuery({
    queryKey: ["messages"],
    queryFn: () =>
      api.get<{
        messages: Array<{
          id: string;
          channel: string;
          status: string;
          origin: string;
          trigger: string | null;
          toAddress: string;
          toName: string | null;
          subject: string | null;
          body: string;
          error: string | null;
          createdAt: string;
          sentAt: string | null;
        }>;
        quota: { used: number; limit: number };
      }>("/messages"),
  });

  const setStyle = useApiMutation({
    mutationFn: (style: "per_invoice" | "per_person") => api.patch("/reminders/style", { style }),
    invalidates: [["reminder-rules"], ["reminder-preview"]],
  });

  const toggleAuto = useApiMutation({
    mutationFn: (enabled: boolean) => api.patch("/settings", { autoRemindersEnabled: enabled }),
    invalidates: [["reminder-rules"], ["me"]],
  });

  const saveRule = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/reminders/rules", input),
    invalidates: [["reminder-rules"], ["reminder-preview"]],
    onSuccess: () => setAdding(false),
  });

  const updateRule = useApiMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) => api.patch(`/reminders/rules/${id}`, input),
    invalidates: [["reminder-rules"], ["reminder-preview"]],
  });

  const deleteRule = useApiMutation({
    mutationFn: (id: string) => api.delete(`/reminders/rules/${id}`),
    invalidates: [["reminder-rules"], ["reminder-preview"]],
  });

  const retry = useApiMutation({
    mutationFn: (id: string) => api.post(`/messages/${id}/retry`),
    invalidates: [["messages"]],
  });

  const data = rules.data;
  const gated = Boolean(data?.gate);

  return (
    <div>
      <PageHeader
        title="Reminders"
        subtitle="Rent falling due, rent overdue, tenancies expiring — chased automatically, or by hand whenever you want."
        description={"What goes out, to whom, and when — with the rules behind it. Preview the next run, send by hand at any time, or edit the wording your tenants actually read."}
        actions={
          <>
            <LinkButton to="/app/reminders/templates" variant="secondary" need="reminder.configure">
              Templates
            </LinkButton>
            {can("reminder.send") ? <LinkButton to="/app/reminders/send" need="reminder.send">Send by hand</LinkButton> : null}
          </>
        }
      />

      {gated ? (
        <div className="mb-5">
          <Alert tone="warn" title="Automatic reminders are not on your plan.">
            You can still select any tenants and send by hand — that never expires. Automatic sending starts on{" "}
            {data?.gate?.needed?.name ?? "a paid plan"}.{" "}
            <Link to="/app/settings/billing" className="font-medium underline">
              See plans
            </Link>
          </Alert>
        </div>
      ) : null}

      <div className="mb-4 flex gap-2 border-b border-ink-200 pb-3">
        {(
          [
            { key: "preview", label: "Next run", count: preview.data?.planned.length },
            { key: "rules", label: "Rules", count: data?.rules.length },
            { key: "log", label: "Sent", count: undefined },
          ] as const
        ).map((option) => (
          <Button
            key={option.key}
            variant={tab === option.key ? "primary" : "secondary"}
            size="sm"
            onClick={() => setTab(option.key)}
          >
            {option.label}
            {typeof option.count === "number" ? <span className="tabular ml-1 opacity-70">{option.count}</span> : null}
          </Button>
        ))}
      </div>

      {tab === "preview" ? (
        <>
          {/*
            How many messages one client gets.

            It lives beside the preview rather than under settings on purpose: the question is
            unanswerable in the abstract and obvious with the next run in front of you. The
            preview's "Covers" column re-draws the moment this changes, so the choice is made
            against the messages it actually affects.
          */}
          <Card className="mb-5">
            <CardTitle hint="A client holding several units would otherwise receive a separate message about each one, in the same run.">
              <span className="inline-flex items-center gap-2">
                <IconOneEnvelope size={18} className="text-ink-400" />
                How many messages a client receives
              </span>
            </CardTitle>

            {setStyle.errorMessage ? <Alert tone="danger">{setStyle.errorMessage}</Alert> : null}

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {(data?.styles ?? []).map((option) => {
                const chosen = data?.style === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!can("reminder.configure") || setStyle.isPending}
                    onClick={() => setStyle.mutate(option.value)}
                    className={`rounded-lg border p-3 text-left transition ${
                      chosen ? "border-brand-500 bg-brand-50/60" : "border-ink-200 hover:border-ink-300"
                    } disabled:cursor-not-allowed disabled:opacity-70`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ink-900">{option.label}</span>
                      {chosen ? <Badge tone="success">In use</Badge> : null}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-ink-500">{option.description}</span>
                  </button>
                );
              })}
            </div>

            {/*
              A worked example rather than a definition. Six units and six arrears is the case the
              setting exists for, and stating it as a number is the only way the difference between
              the two options is legible before either has been tried.
            */}
            <p className="mt-3 text-xs text-ink-500">
              {data?.style === "per_person"
                ? "A client six invoices behind receives one message listing all six, with one total and a link against each."
                : "A client six invoices behind is chased about one of them per run, in a message naming that invoice alone."}
            </p>
          </Card>

          <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle
              hint={
                data?.autoEnabled
                  ? `Checked every hour, sent between ${String(data.sendWindow.from).padStart(2, "0")}:00 and ${String(data.sendWindow.to).padStart(2, "0")}:00 ${data.sendWindow.timezone}.`
                  : "Automatic sending is switched off for this agency."
              }
              action={
                can("reminder.configure") ? (
                  <Button
                    variant={data?.autoEnabled ? "secondary" : "primary"}
                    size="sm"
                    disabled={toggleAuto.isPending}
                    onClick={() => toggleAuto.mutate(!data?.autoEnabled)}
                  >
                    {data?.autoEnabled ? "Turn automatic off" : "Turn automatic on"}
                  </Button>
                ) : null
              }
            >
              What would go out next
            </CardTitle>
            {data?.quota ? (
              <div className="mt-2 max-w-sm">
                <MeterBar
                  value={data.quota.used}
                  max={Number.isFinite(data.quota.limit) ? data.quota.limit : data.quota.used || 1}
                  tone={data.quota.nearLimit ? "warn" : "brand"}
                  /* Pluralised from the allowance, not given a bare "s" — a plan whose monthly
                     allowance is one otherwise reads "of 1 messages" on the screen that is
                     supposed to be telling somebody exactly how many they have left. */
                  label={
                    Number.isFinite(data.quota.limit)
                      ? `${data.quota.used} of ${pluralise(data.quota.limit, "message")} this month`
                      : `${pluralise(data.quota.used, "message")} this month — unlimited`
                  }
                />
              </div>
            ) : null}
          </div>

          {preview.isLoading ? (
            <Spinner />
          ) : preview.error ? (
            <ErrorState error={preview.error} retry={() => preview.refetch()} />
          ) : (preview.data?.planned.length ?? 0) === 0 ? (
            <EmptyState
              title="Nothing is due to be sent right now."
              hint="Everything that matched a rule has already been sent. The next reminder goes out when an invoice reaches its next milestone."
            />
          ) : (
            <Table
              head={
                <tr>
                  <th>Who</th>
                  <th>Why</th>
                  <th>About</th>
                  <th className="num">Covers</th>
                  <th>Channel</th>
                  <th>Reachable</th>
                </tr>
              }
            >
              {preview.data!.planned.map((item, index) => (
                <tr key={`${item.ruleId}-${index}`}>
                  <td>
                    {item.party ? (
                      <Link to={`/app/clients/${item.party.id}`} className="link">
                        {item.party.name}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {triggerInfo(item.trigger as ReminderTrigger).label}
                    <span className="ml-1 text-xs text-ink-400">
                      {triggerInfo(item.trigger as ReminderTrigger).direction === "before" ? "−" : "+"}
                      {item.offsetDays}d
                    </span>
                  </td>
                  <td className="text-ink-500">{fmt.date(item.anchorDate)}</td>
                  <td className="num tabular text-ink-500">
                    {item.covers > 1 ? (
                      <>
                        {item.covers} invoices
                        {item.total === null ? null : (
                          <span className="block text-xs text-ink-400">{fmt.money(item.total)}</span>
                        )}
                      </>
                    ) : (
                      "1 invoice"
                    )}
                  </td>
                  <td className="text-ink-500">{item.channel}</td>
                  <td>
                    {item.deliverable ? (
                      <Badge tone="success">Yes</Badge>
                    ) : (
                      <Badge tone="danger">No {item.channel === "email" ? "email" : "phone"}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
          </Card>
        </>
      ) : null}

      {tab === "rules" ? (
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle
              hint="An offset is days before or after the thing it is about. Each rule fires once per subject."
              action={
                can("reminder.configure") ? (
                  <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
                    {adding ? "Close" : "Add a rule"}
                  </Button>
                ) : null
              }
            >
              Rules
            </CardTitle>

            {adding ? (
              <form
                className="mt-3 grid gap-3 rounded-lg border border-ink-200 p-3 sm:grid-cols-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  saveRule.mutate({
                    trigger: String(form.get("trigger")),
                    offsetDays: Number(form.get("offsetDays")),
                    channel: String(form.get("channel")),
                    templateId: String(form.get("templateId") || "") || null,
                    enabled: true,
                  });
                }}
              >
                {saveRule.errorMessage ? (
                  <div className="sm:col-span-4">
                    <Alert tone="danger">{saveRule.errorMessage}</Alert>
                  </div>
                ) : null}
                <Field label="When" htmlFor="trigger">
                  <Select id="trigger" name="trigger" defaultValue="rent_overdue">
                    {REMINDER_TRIGGERS.map((trigger) => (
                      <option key={trigger.value} value={trigger.value}>
                        {trigger.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Days" htmlFor="offsetDays">
                  <Input id="offsetDays" name="offsetDays" inputMode="numeric" placeholder="7" defaultValue="7" />
                </Field>
                <Field label="Channel" htmlFor="channel">
                  <Select id="channel" name="channel" defaultValue="email">
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                  </Select>
                </Field>
                <Field label="Template" htmlFor="templateId">
                  <Select id="templateId" name="templateId">
                    <option value="">Match the trigger</option>
                    {(data?.templates ?? []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className="sm:col-span-4">
                  <Button type="submit" size="sm" disabled={saveRule.isPending}>
                    {saveRule.isPending ? "Adding…" : "Add rule"}
                  </Button>
                </div>
              </form>
            ) : null}
          </div>

          {rules.isLoading ? (
            <Spinner />
          ) : rules.error ? (
            <ErrorState error={rules.error} retry={() => rules.refetch()} />
          ) : (
            <Table
              head={
                <tr>
                  <th>When</th>
                  <th>Offset</th>
                  <th>Channel</th>
                  <th>Template</th>
                  <th>Enabled</th>
                  <th></th>
                </tr>
              }
            >
              {(data?.rules ?? []).map(({ rule, templateName }) => {
                const info = triggerInfo(rule.trigger as ReminderTrigger);
                return (
                  <tr key={rule.id}>
                    <td>{info.label}</td>
                    <td className="tabular">
                      {info.direction === "before" ? "−" : "+"}
                      {rule.offsetDays} days
                      <span className="block text-xs text-ink-400">{info.direction === "before" ? "before" : "after"}</span>
                    </td>
                    <td className="text-ink-500">{rule.channel}</td>
                    <td className="text-ink-500">{templateName ?? <span className="text-ink-400">by trigger</span>}</td>
                    <td>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-ink-300"
                          checked={rule.enabled}
                          disabled={!can("reminder.configure")}
                          onChange={(event) => updateRule.mutate({ id: rule.id, enabled: event.target.checked })}
                        />
                        {rule.enabled ? "On" : "Off"}
                      </label>
                    </td>
                    <td>
                      {can("reminder.configure") ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm("Remove this rule?")) deleteRule.mutate(rule.id);
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </Table>
          )}
        </Card>
      ) : null}

      {tab === "log" ? (
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle hint="Stored in the exact words the recipient received, so it can be shown in a dispute.">
              Everything sent
            </CardTitle>
          </div>
          {messages.isLoading ? (
            <Spinner />
          ) : messages.error ? (
            <ErrorState error={messages.error} retry={() => messages.refetch()} />
          ) : (messages.data?.messages.length ?? 0) === 0 ? (
            <EmptyState title="Nothing has been sent yet." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {messages.data!.messages.map((message) => (
                <li key={message.id} className="px-4 py-3 sm:px-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <MessageStatusBadge status={message.status} />
                    <span className="text-ink-500">{message.channel}</span>
                    <span className="text-ink-400">·</span>
                    <span className="text-ink-500">{message.origin === "auto" ? "Automatic" : "By hand"}</span>
                    <span className="text-ink-400">·</span>
                    <span className="text-ink-500">{message.toName ?? message.toAddress}</span>
                    <span className="ml-auto text-ink-400">{fmt.dateTime(message.sentAt ?? message.createdAt)}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium">{message.subject ?? message.trigger ?? "Message"}</p>
                  <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-ink-500">{message.body}</p>
                  {message.error ? (
                    <p className="mt-1 flex items-center gap-2 text-xs text-danger-600">
                      {message.error}
                      <Button variant="ghost" size="sm" onClick={() => retry.mutate(message.id)}>
                        Retry
                      </Button>
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </div>
  );
}
